/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { of } from 'rxjs';
import { Cycle } from '@src/app/model/cycle';
import { Field } from '@src/app/shared/field/Field';
import { Period } from '@src/app/model/period';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridField } from '@src/app/shared/grid/grid-field';
import { maxChars, numericRange, required } from '@src/app/shared/validators';
import { CycleService } from '@src/app/modules/budget/cycle.service';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { PeriodService } from '@src/app/modules/budget/period.service';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { ValidationErrors } from '@angular/forms';
import { FormError } from '@src/app/shared/form/form-error/form-error.component';
import { DateHelper } from '@src/app/shared/dateHelper';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { NextCycleAction } from '@src/app/modules/budget/cycle-page/next-cycle-action';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { YearEndAction } from '@src/app/modules/budget/cycle-page/year-end-action';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { SquashPreviousAction } from '@src/app/modules/budget/cycle-page/squash-previous-action';
import { AccountantExportAction } from '@src/app/modules/budget/cycle-page/accountant-export-action';
import { PurchaseDocsAction } from '@src/app/modules/budget/cycle-page/purchase-docs-action';
import { AttachmentGridComponent } from '@src/app/shared/form/file/attachment-grid/attachment-grid.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { BCodePageNavRoute } from '@src/app/routes/bcode-page-nav';
import { CyclePageNavRoute, JournalPageNavRoute } from '@src/app/routes/app-router';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { NewButtonService } from '@src/app/shared/new-button/new-button.service';
import { NavOption } from '@src/app/model/sys/navOption';
import { Router } from '@angular/router';

@Component({
    selector: 'app-cycle-page',
    templateUrl: './cycle-page.component.html',
    styleUrls: ['./cycle-page.component.scss'],
    imports: [FormPageComponent]
})
export class CyclePageComponent extends AbstractPageComponent {
    dataSvc = inject(CycleService);
    cds = inject(ConfirmDialogService);
    private periodSvc = inject(PeriodService);
    private currentUserSvc = inject(CurrentUserService);
    private newButtonSvc = inject(NewButtonService);
    private router = inject(Router);

    showCellOpts = { heading: '', width: '1%', style: 'font-size:small' };

    attachmentGrid = AttachmentGridComponent.make(this.dataSvc);

    childGrid: GridField = new GridField({
        field:
            { label: 'Period Breakdown', value: 'periods', formColumn: 3, visible: Field.formOnly },
        rowFactory: (o: Period) => [
            FieldMaker.id(),
            FieldMaker.rev(),
            FieldMaker.nameControl({ validators: [required, maxChars(20)] }),
            FormDateComponent.make('From', 'from'),
            FormDateComponent.make('To', 'to'),

            FormPicklistComponent.make('Status', 'statusId', 'status', { items: Period.STATUSES }, { readonly: true }),

            FormButtonComponent.make('', '', {
                name: 'action', label: '', type: 'icon', sendServer: false,
                cellOpts: this.showCellOpts,
                disable: o ? o.statusId === Period.STATUS.OPEN.id ? false : true : true,
                calculateValue: () => 'block',
                toolTip: $localize`Block any further transactions for this period (other than by directors)`,
                btnOpts: { clickMethod: this.blockPeriod.bind(this) }
            }),
            FormButtonComponent.make('', '', {
                name: 'action3', label: '', type: 'icon', sendServer: false,
                cellOpts: this.showCellOpts,
                disable: o ? o.statusId === Period.STATUS.CLOSED.id ? false : true : true,
                calculateValue: () => 'edit',
                toolTip: $localize`Reopen this period to allow editing of financial data`,
                btnOpts: { clickMethod: this.unBlockPeriod.bind(this) }
            }),
            FormButtonComponent.make('', '', {
                name: 'action2', label: '', type: 'icon', sendServer: false,
                cellOpts: this.showCellOpts,
                disable: o ? o.statusId === Period.STATUS.CLOSED.id ? false : true : true,
                calculateValue: () => 'lock',
                toolTip: $localize`Lock this period for good and prevent any new or changed transactions being processed`,
                btnOpts: { clickMethod: this.lockPeriod.bind(this) }
            }),            
            FieldMaker.notes(),
            FieldMaker.deleteGridRow(),
        ],
        objFactory: () => of(new Period())
    });

    fromField = FormDateComponent.make('From', 'from', { validators: [required] });
    toField = FormDateComponent.make('To', 'to', { validators: [required] });
    config = new FormConfig({
        navRoute: CyclePageNavRoute,
        docPath: 'omc-books/budgeting/cycles',
        title: $localize`Budget Cycle`,
        help: $localize`The budget cycle, budgets are planned, approved and billed for one cycle at a time.`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                FieldMaker.nameControl({ validators: [required, maxChars(20)] }),
                this.fromField,
                this.toField,

                FormNumberComponent.make('Discount for Direct Debit Payments', 'mandateDiscount',
                    { format: 'percent', formatParms: '2.3-3', step: .01 },
                    {
                        formColumn: 2, validators: [numericRange(0, 100)], disable: true,
                        hint: 'Direct debit processing not live yet, so discount not available'
                    }
                ),
                FormDateComponent.make('First DD Payments Taken', 'firstPayments', { formColumn: 2}),
                FormDateComponent.make('Last DD Payments Taken', 'lastPayments', { formColumn: 2 }),

                FormNumberComponent.make('Discount for full on time payment', 'fullPayDiscount',
                    { format: 'percent', formatParms: '2.3-3', step: .01 },
                    {
                        formColumn: 2, validators: [numericRange(0, 100)],
                        hint: 'Automatically applies discount if charges are settled on time, and builds cost into the budget'
                    }
                ),
                FormDateComponent.make('Date to qualify for full payment discount', 'fullPayBy', { formColumn: 2 }),

                FieldMaker.notes(),
                this.childGrid
            ], formLayout: [{ cells: [{ width: '25%' }, { width: '25%' }, { width: '50%', pageTab: 'yes' }] }],
            formValidator: [this.validatePeriods.bind(this)]
        }),
        tabFields: [this.getYearEndsGrid(), this.attachmentGrid],
        service: this.dataSvc,
        mode: 'list',
        actions: [new YearEndAction(), new SquashPreviousAction(),
            new AccountantExportAction(), new PurchaseDocsAction()],
        objectFactory: this.newCustomCycle.bind(this),
        beforeList: this.offerNextCycle.bind(this),
    });

    private newCustomCycle() {
        if (!this.currentUserSvc.hasUber()) {
            this.cds.alert($localize`Custom Cycle`,
                $localize`You probably do not want to create a custom cycle. `
                + $localize`To create the next financial year, pick Next Cycle from the New menu.`,
                () => this.router.navigate([CyclePageNavRoute.getListRouteName()]));
        }
        return of(new Cycle());
    }

    private offerNextCycle(cycles: Cycle[]) {
        const newOptions = this.newCycleOptions(this.cycleToCreateNextFrom(cycles));
        this.newButtonSvc.setupButton(this.config.title, this.config.pathNew(), this.config.allowNew, newOptions);
        return cycles;
    }

    private cycleToCreateNextFrom(cycles: Cycle[]): Cycle {
        const latest = cycles.reduce((latest: Cycle, c) => !latest || c.to > latest.to ? c : latest, null);
        const alreadyHasNext = latest && cycles.some(c => c.previousId === latest.id);
        return alreadyHasNext ? null : latest;
    }

    private newCycleOptions(latest: Cycle): NavOption[] {
        const options: NavOption[] = [];
        if (latest) {
            options.push({
                id: 'createNext', name: $localize`Next Cycle after ${latest.name}`, icon: 'skip_next',
                action: () => this.confirmCreateNext(latest)
            });
        }
        if (this.currentUserSvc.hasUber()) {
            options.push({
                id: 'customCycle', name: $localize`Custom Cycle`, icon: 'edit_calendar',
                basePath: CyclePageNavRoute.newUrl
            });
        }
        return options;
    }

    private confirmCreateNext(latest: Cycle) {
        const nextCycleAction = new NextCycleAction();
        this.cds.open(nextCycleAction.name, nextCycleAction.approvalText,
            () => nextCycleAction.action(latest).subscribe());
    }

    private yearEndRowFactory() {
        return [
            FormNavLinkComponent.makeDetail('Journal', 'parentRef', 'parentId', JournalPageNavRoute),
            FormDateTimeComponent.make('Created', 'createdAt', { readonly: true }),
            FormNavLinkComponent.makeDetail('Budget Code', 'bCodeName', 'bCodeId', BCodePageNavRoute),
            FormTextComponent.make('Sort', 'bCodeSort').override({ readonly: true }),
            FormNumberComponent.makeCurrency('Credit', 'credit').override({ readonly: true }),
            FormNumberComponent.makeCurrency('Debit', 'debit').override({ readonly: true }),
            FieldMaker.spacer()
        ];
    }

    private getYearEndsGrid() {
        return new GridField({
            field:
                { label: 'Year End Balances', value: 'yearEnds', visible: Field.formOnly, readonly: true },
            rowFactory: this.yearEndRowFactory.bind(this)
        });
    }

    validatePeriods(): ValidationErrors {
        if (!this.childGrid.control) {
            return null;
        }
        let prevEnd = '';
        for (const gridRow of this.childGrid.control.gridRows()) {
            if (!gridRow.get('from') || !gridRow.get('to')) {
                return null; // wasn't initialized...
            }
            if (Field.isEmpty(prevEnd)) {
                if (gridRow.get('from').value !== this.fromField.control.value) {
                    return FormError.reportError('StartDateNotMatching',
                        $localize`Start Date of first period does not match start date of cycle`);
                }
            } else {
                if (DateHelper.isoDaysBetween(prevEnd, gridRow.get('from').value) !== 1) {
                    return FormError.reportError('DatesNonContiguous',
                        $localize`Start data of period does not follow directly after last day of previous period`);
                }
            }
            prevEnd = gridRow.get('to').value
        }
        if (prevEnd !== this.toField.control.value) {
            return FormError.reportError('EndDateNotMatching',
                $localize`End date of last period does not match end date of cycle`);
        }

        return null;
    }

    calcToolTip(o: Period) {
        if (o) {
            if (o.statusId === Period.STATUS.OPEN.id) {
                return $localize`Block any further transactions for this period (other than by directors)`;
            } else if (o.statusId === Period.STATUS.CLOSED.id) {
                return $localize`Lock this period for good and prevent any new or changed transactions being processed`
            } else {
                return $localize`Period locked and closed - No new transactions or changes possible`;
            }
        }
    }

    calcPeriodIcon(o: Period) {
        if (o.statusId === Period.STATUS.OPEN.id) {
            return 'block';
        } else if (o.statusId === Period.STATUS.CLOSED.id) {
            return 'lock'
        } else {
            return 'lock';
        }
    }

    blockPeriod(ctl: AppFormControl) {
        const period = ctl.getRow().focus as Period;
        const action = $localize`Close Period`;
        const text = $localize`Close period now and do not allow any further new or changed transactions?`
        this.cds.open(action, text, () => {
            this.periodSvc.block(period).subscribe(updatedPeriod => {
                if (updatedPeriod) {
                    ctl.getRow().refreshFrom(updatedPeriod);
                }
            });
        });
    }

    unBlockPeriod(ctl: AppFormControl) {
        const period = ctl.getRow().focus as Period;
        const action = $localize`Open Period`;
        const text = $localize`Reopen period now and allow editing of financial data?`
        this.cds.open(action, text, () => {
            this.periodSvc.unblock(period).subscribe(updatedPeriod => {
                if (updatedPeriod) {
                    ctl.getRow().refreshFrom(updatedPeriod);
                }
            });
        });
    }

    lockPeriod(ctl: AppFormControl) {
        const period = ctl.getRow().focus as Period;
        const action = $localize`Lock Period`;
        const text = $localize`
        *****Lock period****, so not even directors can make any further changes? 
        ***** THIS CANNOT BE UNDONE ****
        you should only do this after accounts are filed with CRO
        `
        this.cds.open(action, text, () => {
            this.periodSvc.lock(period).subscribe(updatedPeriod => {
                if (updatedPeriod) {
                    ctl.getRow().refreshFrom(updatedPeriod);
                }
            });
        });
    }
}
